import type {
  MeasurementLabelSettings,
  Preference,
  UserInitResponse,
} from '@/types/user'
import { DEFAULT_UNITS, type MeasurementUnits } from '@/utilities/measurements'

export const MEASUREMENT_UNITS_PREFERENCE_KEY = 'map.measurement.units'
export const MEASUREMENT_LABELS_PREFERENCE_KEY = 'map.measurement.labels'

export const DEFAULT_MEASUREMENT_LABEL_SETTINGS: MeasurementLabelSettings = {
  point: { latitude: true, longitude: true },
  line: { segmentLengths: true, totalLength: true },
  polygon: { segmentLengths: true, perimeter: true, area: true },
}

export type { MeasurementLabelSettings } from '@/types/user'

export const isMeasurementUnits = (value: unknown): value is MeasurementUnits =>
  value === 'imperial' || value === 'metric'

export const resolveMeasurementUnits = (
  preferences: Preference | null | undefined
): MeasurementUnits => {
  const value = preferences?.[MEASUREMENT_UNITS_PREFERENCE_KEY]?.[0]?.value
  return isMeasurementUnits(value) ? value : DEFAULT_UNITS
}

const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean'

export const normalizeMeasurementLabelSettings = (
  value: unknown
): MeasurementLabelSettings => {
  if (value == null || typeof value !== 'object')
    return DEFAULT_MEASUREMENT_LABEL_SETTINGS

  const candidate = value as Partial<MeasurementLabelSettings>
  return {
    point: {
      latitude: isBoolean(candidate.point?.latitude)
        ? candidate.point.latitude
        : true,
      longitude: isBoolean(candidate.point?.longitude)
        ? candidate.point.longitude
        : true,
    },
    line: {
      segmentLengths: isBoolean(candidate.line?.segmentLengths)
        ? candidate.line.segmentLengths
        : true,
      totalLength: isBoolean(candidate.line?.totalLength)
        ? candidate.line.totalLength
        : true,
    },
    polygon: {
      segmentLengths: isBoolean(candidate.polygon?.segmentLengths)
        ? candidate.polygon.segmentLengths
        : true,
      perimeter: isBoolean(candidate.polygon?.perimeter)
        ? candidate.polygon.perimeter
        : true,
      area: isBoolean(candidate.polygon?.area) ? candidate.polygon.area : true,
    },
  }
}

export const resolveMeasurementLabelSettings = (
  preferences: Preference | null | undefined
): MeasurementLabelSettings => {
  const value = preferences?.[MEASUREMENT_LABELS_PREFERENCE_KEY]?.[0]?.value
  return normalizeMeasurementLabelSettings(value)
}

const withMeasurementPreference = <T>(
  response: UserInitResponse | undefined,
  key:
    | typeof MEASUREMENT_UNITS_PREFERENCE_KEY
    | typeof MEASUREMENT_LABELS_PREFERENCE_KEY,
  name: string,
  value: T
): UserInitResponse | undefined => {
  if (!response?.data) return response

  const previousEntry = response.data.preferences?.[key]?.[0]
  return {
    ...response,
    data: {
      ...response.data,
      preferences: {
        ...response.data.preferences,
        [key]: [{ id: previousEntry?.id ?? 0, name, value }],
      },
    },
  }
}

export const withMeasurementUnits = (
  response: UserInitResponse | undefined,
  units: MeasurementUnits
): UserInitResponse | undefined => {
  return withMeasurementPreference(
    response,
    MEASUREMENT_UNITS_PREFERENCE_KEY,
    'units',
    units
  )
}

export const withMeasurementLabelSettings = (
  response: UserInitResponse | undefined,
  settings: MeasurementLabelSettings
): UserInitResponse | undefined =>
  withMeasurementPreference(
    response,
    MEASUREMENT_LABELS_PREFERENCE_KEY,
    'labels',
    settings
  )
